<template>
  <div
    v-if="filters.length > 0"
    :class="$style.container"
  >
    <DropDownMultiSelectSearch
      v-for="(filter) in filters"
      :key="`${filter.type}_${getUniqueId()}`"
      :disabled="getFilterDisabledState(filter.type)"
      :emptyValueText="getEmptyTextValue(filter.type)"
      :name="filter.title"
      :options="filter.options"
      :showFullValueText="true"
      :value="filter.values"
      @input="(value) => {handleSelectChange(filter, value)}"
    />
    <Button
      :class="$style.clearFilters"
      hasNoBackground
      hasOutline
      :isDisabled="getClearFilterButtonDisabledState()"
      :isLoading="false"
      :isMarketingStyle="false"
      :isMatchText="false"
      text="clear all"
      type="primary"
      @click="clearFilters"
    />
  </div>
</template>

<script>
import { Button } from '@jumpcloud/ui-components';
import {
  FilterApiRefs, FilterAttributes, FilterDisableRefs, FilterTitles, FilterTypes,
} from 'Reports/constants/reportsConstants';
import { uniqueId } from 'lodash';
import DropDownMultiSelectSearch from 'client/components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import ReportMetadata, { ReportTypes } from 'client/Reports/ReportMetadata';

export default {
  name: 'ReportFilters',

  components: {
    Button,
    DropDownMultiSelectSearch,
  },

  props: {
    reportType: {
      type: String,
      default: ReportTypes.userToDevice,
      validator: value => typeof ReportTypes[value] === 'string',
    },
  },

  data: () => ({
    disabledFilters: [],
    filters: [],
  }),

  watch: {
    reportType: {
      immediate: true,
      handler(newVal) {
        this.reportType = newVal;
        this.filters = [];
        this.disabledFilters = [];
        this.renderFilters();
      },
    },
  },

  methods: {
    clearFilters() {
      this.filters = this.filters.map(filter => ({ ...filter, values: [] }));
      this.disabledFilters = [];
      this.$emit('filterEvent');
    },

    async fetchFilterOptions(filterType) {
      try {
        const { results } = await FilterApiRefs[filterType].fetch();
        return results;
      } catch {
        return Promise.resolve([]);
      }
    },

    formatFiltersForEvent() {
      const filterEventObjects = [];

      const filts = this.filters.filter(filter => filter.values.length > 0);

      filts.forEach(f => {
        filterEventObjects.push({
          type: f.type,
          ids: f.values,
        });
      });

      return filterEventObjects;
    },

    formatSelectData(data, filterType, nameAttribute, valueAttribute) {
      // Some of the data contain options we do not want displayed
      const ignoredValues = ['All Devices', 'All Users'];
      // Format the data in the multiselectdrop down format
      return data.map(item => ({
        name: filterType === FilterTypes.users ? `${item.firstname} ${item.lastname}` : item[nameAttribute],
        value: item[valueAttribute],
      })).filter(item => !ignoredValues.includes(item.name));
    },

    getClearFilterButtonDisabledState() {
      let isDisabled = false;

      this.filters.every(filter => {
        isDisabled = !filter.values || filter.values.length < 1;
        return filter.values.length < 1;
      });

      return isDisabled;
    },

    getEmptyTextValue(type) {
      return this.getFilterDisabledState(type) ? 'n/a' : 'all';
    },

    getFilterDisabledState(filterType) {
      return this.disabledFilters.includes(filterType);
    },

    getUniqueId: () => uniqueId(),

    handleSelectChange(filter, values) {
      const tempFilter = filter;
      tempFilter.values = values;

      if (tempFilter.values.length > 0) {
        FilterDisableRefs[tempFilter.type].forEach(targetFilter => {
          if (this.disabledFilters.indexOf(targetFilter) === -1) {
            this.disabledFilters.push(targetFilter);
          }
        });
      } else {
        // remove from disabledFilters list if filters removed
        FilterDisableRefs[tempFilter.type].forEach(targetFilter => {
          const index = this.disabledFilters.indexOf(targetFilter);
          this.disabledFilters.splice(index, 1);
        });
      }

      this.$emit('filterEvent', this.formatFiltersForEvent());
    },

    async renderFilters() {
      const filterTypes = ReportMetadata[this.reportType].filters;

      const tempFilters = filterTypes.map(filterType => ({
        title: FilterTitles[filterType],
        type: FilterTypes[filterType],
        options: [],
        values: [],
      }));

      this.filters = await Promise.all(tempFilters.map(async (filter) => ({
        ...filter,
        options: this.formatSelectData(
          await this.fetchFilterOptions(filter.type),
          filter.type,
          FilterAttributes[filter.type].name,
          FilterAttributes[filter.type].value,
        ),
      })));
    },
  },
};
</script>

<style module>
.container {
  display: flex;
}

.clearFilters {
  margin-left: var(--jc-spacer-x-small);
}

.container > div {
  margin-right: var(--jc-spacer-x-small);
}
</style>
